import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "My new favourite Javascript trick",
  "description": "",
  "published": "2014-06-26T00:00:00.000Z",
  "redirect_from": ["/blog/my-new-favourite-javascript-trick/swizec/6585"],
  "categories": "Callback, Code, JavaScript, Return statement, Server, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Using returns and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Callback_%28computer_programming%29",
        "title": "Callback (computer programming)"
      }}>{`callbacks`}</a>{` in the same function.`}</p>
    <p>{`Sounds like crazy talk I know, but hear me out, I have good reason. I think.`}</p>
    <p>{`Let's say you want to make several `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Transmission_Control_Protocol",
        "title": "Transmission Control Protocol"
      }}>{`TCP`}</a>{` servers in a node.js application. Have to listen on multiple ports or whatever. Using a factory function is your best bet to avoiding code repetition, right?`}</p>
    <p>{`You end up with something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var server = function (port, callback) {
    var server = net.createServer(function (connection) {});

    server.on("connection", function (socket) {
        socket.on("data", // data stuff);

        socket.on("error", function () {
            console.log("error?", arguments);
            socket.destroy();
        });
        socket.on("close", // cleanup stuff);
    });

    server.on("listening", callback);
    server.listen(port);
};
`}</code></pre>
    <p>{`Simple. Call a function, give it a port, get notified when server is ready to listen. Never be able to touch the server again.`}</p>
    <p>{`Wait, that's not good. What if you want to reference the server later? To close it, for instance.`}</p>
    <p>{`That's where using returns alongside callbacks comes in.`}</p>
    <p>{`Just adding a `}<inlineCode parentName="p">{`return server`}</inlineCode>{` at the bottom of that function lets us do something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var make_servers = function (callback) {
  this.servers = {};

  async.each(
    [5000, 5001, 5002],
    _.bind(function (port, callback) {
      this.servers[port] = server(port, callback);
    }, this),
    function (err) {
      callback();
    }
  );

  return this;
};
`}</code></pre>
    <p>{`I sneakily added the async and underscore libraries because they make life easier.`}</p>
    <p><inlineCode parentName="p">{`make_servers`}</inlineCode>{` will generate three servers listening on ports `}<inlineCode parentName="p">{`5000`}</inlineCode>{` to `}<inlineCode parentName="p">{`5002`}</inlineCode>{`. The async library helped us ensure the main callback is only called once all the servers are ready to listen, and using `}<inlineCode parentName="p">{`_.bind`}</inlineCode>{` let us bind server generation to the current scope.`}</p>
    <p>{`When this function is done it returns its scope, which now includes references to all the servers, and it will tell your code to keep going when all the servers are ready.`}</p>
    <p>{`You'd use it something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var stuff = make_servers(function () { // all servers listening });

// stuff.servers can access all servers
`}</code></pre>
    <p>{`If you bind all the callbacks inside `}<inlineCode parentName="p">{`server`}</inlineCode>{` to current scope you can even keep track of connections. You'd end up with something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var server = function (port) {
    var server = net.createServer(function (connection) {});

    server.on("connection", _.bind(function (socket) {
        socket.id = shortid.generate();
        this.connections[port][socket.id] = socket;

        socket.on("data", // data stuff);

        socket.on("error", function () {
            console.log("error?", arguments);
            socket.destroy();
        });
        socket.on("close", _.bind(function () {
            delete this.connections[port][socket.id];
        }, this));
    }, this));

    return function (callback) {
        server.on("listening", callback);
        server.listen(port);
        return server;
    };
};
`}</code></pre>
    <p>{`Not much has changed. More things were bound to `}<inlineCode parentName="p">{`this`}</inlineCode>{` and the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Return_statement",
        "title": "Return statement"
      }}>{`return value`}</a>{` became a function because I feel `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Partial_application",
        "title": "Partial application"
      }}>{`partial application`}</a>{` makes this code cleaner.`}</p>
    <p>{`You'd call the `}<inlineCode parentName="p">{`server`}</inlineCode>{` factory like this now: `}<inlineCode parentName="p">{`this.servers[port] = server(port)(callback)`}</inlineCode>{`. The main benefit of this approach is that we can generate servers in a loop and activate them at a later time.`}</p>
    <p>{`We've essentially decoupled server generation and server startup. Might come in handy.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`stuff`}</inlineCode>{` object from before is now going to have a `}<inlineCode parentName="p">{`stuff.connections`}</inlineCode>{` as well, which references all currently open connections to each port. Neat!`}</p>
    <p>{`Another trick I sneaked into these examples is `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`Javascript`}</a>{`'s powerful `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scope_%28computer_science%29",
        "title": "Scope (computer science)"
      }}>{`dynamic scoping`}</a>{`. Getting semi-random functions to run in the same scope like that can really clean up your code.`}</p>
    <p>{`At the expense of being almost too clever sometimes. Use at your own peril.`}</p>
    <p>{`What do you think, is there a cleaner way to implement something like this?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      